import React from 'react';
import { FormControl, FormHelperText } from '@mui/material';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { PasswordField, GenericPubFormLayout, TextField, MuiLink as Link } from '@askporter/component-library';
import { TFunction } from '@askporter/config-provider';
import { validEmailRegex } from '@askporter/utils';
import { SignInFormValues } from '../../types';

interface SignInFormProps {
  logo?: string;
  isSmallDevice: boolean;
  handleSubmit: () => void;
  errors: FieldErrors<SignInFormValues>;
  control: Control<SignInFormValues>;
  tenantName?: string;
  botName?: string;
  t: TFunction<'translation'>;
  hideCreateAccount: boolean;
  loading?: boolean;
}

const SignInForm: React.FC<SignInFormProps> = ({
  logo,
  isSmallDevice,
  handleSubmit,
  errors,
  control,
  tenantName,
  hideCreateAccount,
  botName,
  t,
  loading,
}: SignInFormProps) => {
  return (
    <GenericPubFormLayout
      title={`${t('ns.sign_in:log_in')}`}
      introText={`${t('ns.sign_in:subtitle', {
        bot_name: botName,
      })}`}
      linkText={hideCreateAccount ? '' : `${t('ns.sign_in:create_account')}`}
      linkHref="/sign-up"
      linkTestId="create-account-link"
      buttonText={`${t('ns.sign_in:log_in')}`}
      buttonOnClick={handleSubmit}
      buttonTestId="sign-in"
      buttonType="submit"
      logo={logo}
      tenantName={tenantName}
      isSmallDevice={isSmallDevice}
      isLoading={loading}
    >
      <div data-testid="sign-in-form">
        <FormControl required={true} fullWidth>
          <Controller
            name="email"
            rules={{
              required: `${t('ns.common:email:error:required')}`,
              pattern: {
                value: validEmailRegex,
                message: `${t('ns.common:email:error:valid')}`,
              },
            }}
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <TextField
                id="email"
                variant="filled"
                error={!!errors.email}
                inputProps={{ 'data-testid': 'email' }}
                placeholder={t('ns.common:email:placeholder')}
                label={t('ns.common:email:label')}
                value={value}
                onChange={onChange}
                required={true}
              />
            )}
          />
        </FormControl>
        {!!errors.email && (
          <FormHelperText error={true} data-testid="email-error">
            {errors.email.message}
          </FormHelperText>
        )}

        <Controller
          name="password"
          rules={{
            required: `${t('ns.common:password:error:required')}`,
          }}
          control={control}
          defaultValue=""
          render={({ field }) => {
            return (
              <PasswordField
                {...field}
                fullWidth
                required={true}
                error={!!errors.password}
                t={t}
                label={t('ns.common:password:label')}
              />
            );
          }}
        />

        {!!errors.password && (
          <FormHelperText error={true} data-testid="password-error">
            {errors.password.message}
          </FormHelperText>
        )}
        <Link href="/reset-password">{`${t('ns.sign_in:forgot_password')}`}</Link>
      </div>
    </GenericPubFormLayout>
  );
};

export default SignInForm;
